/**
 * What an empty calendar cell can become (#2653).
 *
 * Clicking free time used to open the create wizard straight away. JV asked
 * for the other thing a foreman does with a free slot: put an already-quoted
 * job into it. So the click first asks which — a new work order, or an open
 * quote — and the quote path shows the open quotes to choose from before the
 * accept-and-schedule dialog opens with the clicked slot prefilled.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { format, parseISO } from 'date-fns';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useBookings, type BookingListItem } from '@/hooks/useBookings';
import { dateFnsLocale } from '@/utils/locale';

// ─── Choice: new work order or an open quote ────────────────────────────────

interface EmptySlotChoiceDialogProps {
  open: boolean;
  date: Date | null;
  mechanicName: string | null;
  onNewWorkOrder: () => void;
  onBookQuote: () => void;
  onClose: () => void;
}

export function EmptySlotChoiceDialog({
  open,
  date,
  mechanicName,
  onNewWorkOrder,
  onBookQuote,
  onClose,
}: EmptySlotChoiceDialogProps) {
  const { t } = useTranslation('calendar');
  const { t: tc } = useTranslation('common');

  return (
    <ResponsiveDialog open={open} onClose={onClose} mobileTitle={t('emptySlot.title')} maxWidth="xs" fullWidth>
      <DialogTitle>{t('emptySlot.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
          {date && (
            <Typography variant="body2" color="text.secondary">
              {format(date, 'EEEE d.M.yyyy HH:mm', { locale: dateFnsLocale() })}
            </Typography>
          )}
          {mechanicName && (
            <Typography variant="body2" color="text.secondary">
              {t('emptySlot.mechanic', { name: mechanicName })}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={onNewWorkOrder} fullWidth>
            {t('newWorkOrder')}
          </Button>
          <Button variant="outlined" startIcon={<RequestQuoteIcon />} onClick={onBookQuote} fullWidth>
            {t('emptySlot.bookQuote')}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tc('cancel')}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

// ─── Picker: which open quote ───────────────────────────────────────────────

/** A quote is bookable while it is a quote and has not passed its validity. */
export function isOpenQuote(item: Pick<BookingListItem, 'status' | 'quoteExpiresAt'>, now = Date.now()): boolean {
  if (item.status !== 'quote') return false;
  if (!item.quoteExpiresAt) return true;
  return new Date(item.quoteExpiresAt).getTime() > now;
}

type QuotePickerItem = BookingListItem & {
  quoteValidityDays?: number | null;
  effectiveQuoteExpiresAt?: string | null;
};

function quotePickerValidityLabel(
  item: QuotePickerItem,
  tCalendar: (key: string, vars?: Record<string, unknown>) => string,
  tWorkorders: (key: string, vars?: Record<string, unknown>) => string,
): string {
  if (item.quoteValidityDays != null && item.effectiveQuoteExpiresAt == null && item.quoteExpiresAt == null) {
    return tWorkorders('quoteQueue.validForDaysFromSend', { count: item.quoteValidityDays });
  }
  const iso = item.effectiveQuoteExpiresAt ?? item.quoteExpiresAt;
  if (iso) {
    return tCalendar('quotePicker.validUntil', {
      date: format(parseISO(iso), 'd.M.yyyy', { locale: dateFnsLocale() }),
    });
  }
  return tCalendar('quotePicker.noExpiry');
}

interface OpenQuotePickerDialogProps {
  open: boolean;
  onSelect: (quoteId: string) => void;
  onClose: () => void;
}

export function OpenQuotePickerDialog({ open, onSelect, onClose }: OpenQuotePickerDialogProps) {
  const { t } = useTranslation('calendar');
  const { t: tWorkorders } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const [query, setQuery] = useState('');

  const quotes = useBookings(
    { status: 'quote', sortBy: 'createdAt', sortOrder: 'desc', limit: 50 },
    { enabled: open },
  );

  const items = useMemo(() => {
    const all = (quotes.data?.pages ?? []).flatMap((page) => page.data).filter((item) => isOpenQuote(item));
    const needle = query.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((item) =>
      [item.workOrderNumber ?? '', item.vehicleRegNumber, item.customerName, item.serviceType ?? '']
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [quotes.data, query]);

  return (
    <ResponsiveDialog open={open} onClose={onClose} mobileTitle={t('quotePicker.title')} maxWidth="sm" fullWidth>
      <DialogTitle>{t('quotePicker.title')}</DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          fullWidth
          autoFocus
          value={query}
          onChange={(event) => { setQuery(event.target.value); }}
          placeholder={t('quotePicker.search')}
          slotProps={{ htmlInput: { 'aria-label': t('quotePicker.search') } }}
          sx={{ mb: 1.5 }}
        />
        {quotes.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {t('quotePicker.empty')}
          </Typography>
        ) : (
          <List dense disablePadding>
            {items.map((item) => (
              <ListItemButton key={item.id} onClick={() => { onSelect(item.id); }}>
                <ListItemText
                  primary={[item.workOrderNumber, item.vehicleRegNumber].filter(Boolean).join(' · ')}
                  secondary={[item.customerName, item.serviceType].filter(Boolean).join(' · ')}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={quotePickerValidityLabel(item, t, tWorkorders)}
                />
              </ListItemButton>
            ))}
          </List>
        )}
        {quotes.hasNextPage && (
          <Button
            size="small"
            onClick={() => { void quotes.fetchNextPage(); }}
            disabled={quotes.isFetchingNextPage}
            sx={{ mt: 1 }}
          >
            {t('quotePicker.loadMore')}
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tc('cancel')}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
